import {Text} from '@/components/ui/text/Text'
import {useNotificationDescription} from '@/features/notifications/hooks/useNotificationDescription'
import {UserNotification} from '@/features/notifications/types'
import {useTranslation} from '@/translations/i18n'
import {ConversationKind, ConversationKindBadge} from '../conversation-kind-badge/ConversationKindBadge'
import {
    StyledConversationContextHeader,
    StyledConversationContextReasonButton,
    StyledConversationContextReasonDot,
    StyledConversationContextReasonRow
} from './style'

/**
 * Why the operator is here, in the notification's own words. The backend composes that sentence
 * server-side, so it already names the sender and — on a new message — the conversation, which
 * on notifications predating `extraData.title` is the only place its name appears at all.
 */
const ConversationContextReason = ({
    notification,
    onOpenConversation
}: {
    notification: UserNotification
    onOpenConversation?: () => void
}) => {
    const {t} = useTranslation()
    const {title, triggeredByPersonName} = useNotificationDescription(notification)
    const attribution = triggeredByPersonName
        ? ` ${t('notifications:context_attribution', {name: triggeredByPersonName})}`
        : ''

    const reason = (
        <>
            <StyledConversationContextReasonDot />
            <Text size="textSm" palette="neutral" shade="700">
                {notification.notification.bodyTemplate?.trim() || `${title}${attribution}`}
            </Text>
        </>
    )

    // Clickable only when it has somewhere to go: the conversation is known and is not the one
    // already open. Nothing else about the line changes, so it never offers a dead click.
    return onOpenConversation ? (
        <StyledConversationContextReasonButton
            type="button"
            onClick={onOpenConversation}
            title={t('conversations:open_referenced_conversation')}
        >
            {reason}
        </StyledConversationContextReasonButton>
    ) : (
        <StyledConversationContextReasonRow>{reason}</StyledConversationContextReasonRow>
    )
}

interface ConversationContextHeaderProps {
    kind: ConversationKind
    /** The conversation's name, or null when nothing at hand carries it (older notifications) */
    title: string | null
    patientName?: string
    /** The notification that sent the operator here, when there is one */
    notification?: UserNotification
    /** Opens the conversation that notification is about; omitted when it is already open */
    onOpenConversation?: () => void
}

/**
 * What the operator is looking at, stated by the panel itself: kind of conversation, name,
 * patient, and the task that sent them here.
 *
 * It is the panel's only such statement, and it holds in every case the header cannot cover —
 * the work-context bar takes the header's place when the panel is opened from a notification,
 * and a deep link that resolves to no conversation leaves the panel on its list with a header
 * that names neither the task nor its conversation.
 */
export const ConversationContextHeader = ({
    kind,
    title,
    patientName,
    notification,
    onOpenConversation
}: ConversationContextHeaderProps) => {
    const {t} = useTranslation()

    return (
        <StyledConversationContextHeader direction="column" align="start" gap={1} fullWidth>
            <ConversationKindBadge kind={kind} />
            {title && (
                <Text size="textLg" weight="500" palette="neutral" shade="900">
                    {title}
                </Text>
            )}
            {patientName && (
                <Text size="textSm" weight="500" palette="neutral" shade="700">
                    {t('conversations:patient_label', {name: patientName})}
                </Text>
            )}
            {notification && (
                <ConversationContextReason notification={notification} onOpenConversation={onOpenConversation} />
            )}
        </StyledConversationContextHeader>
    )
}
